import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { OnsiteLiveTeam } from '@/types/oscar';
import { inputCls } from '../BranchRelationships/styles';

const teamLabel = (t: OnsiteLiveTeam) => `${t.TeamId} — ${t.TeamCode}`;

/**
 * Type-to-search team picker over the teams already loaded (no API call). The
 * value is a team id as a string, '' for none. Typing filters on the number or
 * the name; only a pick commits, and a blur or Escape puts the chosen team back,
 * so the value is always a real team. `emptyLabel` adds a first "none" option
 * (e.g. "All teams" on the filter). Same mousedown-preventDefault trick as
 * EntityPicker so a click on an option doesn't blur first.
 */
export function TeamCombobox({
  teams, value, onChange, emptyLabel, placeholder = 'Search teams…', ariaLabel, className = '',
}: {
  teams: OnsiteLiveTeam[];
  value: string;
  onChange: (teamId: string) => void;
  emptyLabel?: string;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const selected = teams.find((t) => String(t.TeamId) === value);
  const selectedText = selected ? teamLabel(selected) : (emptyLabel && value === '' ? emptyLabel : '');
  const [text, setText] = useState(selectedText);
  const [typed, setTyped] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => { if (!open) setText(selectedText); }, [selectedText, open]);

  type Option = { id: string; label: string };
  const options = useMemo<Option[]>(() => {
    const q = typed ? text.trim().toLowerCase() : '';
    const matches = teams
      .filter((t) => q === '' || String(t.TeamId).startsWith(q) || t.TeamCode.toLowerCase().includes(q) || teamLabel(t).toLowerCase().includes(q))
      .map((t) => ({ id: String(t.TeamId), label: teamLabel(t) }));
    return emptyLabel && q === '' ? [{ id: '', label: emptyLabel }, ...matches] : matches;
  }, [teams, text, typed, emptyLabel]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const show = () => {
    setTyped(false);
    // Index into the unfiltered list, which is what the next render shows.
    const at = teams.findIndex((t) => String(t.TeamId) === value);
    setActive(emptyLabel ? at + 1 : Math.max(0, at));
    setOpen(true);
  };
  const close = () => { setOpen(false); setTyped(false); setText(selectedText); };
  const pick = (o: Option) => { onChange(o.id); setOpen(false); setTyped(false); setText(o.label); };

  return (
    <div className={`relative ${className}`}>
      <input
        value={text}
        onChange={(e) => { setText(e.target.value); setTyped(true); setActive(0); setOpen(true); }}
        onFocus={(e) => { e.target.select(); show(); }}
        onClick={() => { if (!open) show(); }}
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) show(); else setActive((i) => Math.min(i + 1, options.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter' && open) { e.preventDefault(); if (options[active]) pick(options[active]); }
          else if (e.key === 'Escape' && open) { e.preventDefault(); close(); }
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
        className={`${inputCls} w-full min-w-0 pr-7 font-normal`}
      />
      {/* An icon, not a ▾ character: text here would become part of the wrapping label's name. */}
      <svg aria-hidden viewBox="0 0 20 20" fill="currentColor" className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-darkGrey/60">
        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
      </svg>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full min-w-[16rem] overflow-y-auto rounded border border-border bg-white py-1 text-sm font-normal shadow-md"
        >
          {options.length === 0 && <li className="px-3 py-2 text-darkGrey/60">No team matches “{text.trim()}”.</li>}
          {options.map((o, i) => (
            <li
              key={o.id || 'none'}
              id={`${listId}-${i}`}
              data-index={i}
              role="option"
              aria-selected={o.id === value}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o)}
              className={`cursor-pointer px-3 py-1.5 ${i === active ? 'bg-[#E6ECF2]' : ''} ${o.id === value ? 'font-semibold text-navy' : 'text-darkGrey'}`}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
